import { ANON_USER, PERMISSIONS } from '@23degrees/shared-code/config/permissions';
import { Injectable, inject } from '@angular/core';
import { Query, toBoolean } from '@datorama/akita';
import get from 'lodash/get';
import { filter, map } from 'rxjs/operators';
import { AuthState, AuthStore } from './auth.store';

import { Product } from '@23degrees/shared/models/pricing/products.model';
import * as storage from './storage';

@Injectable({ providedIn: 'root' })
export class AuthQuery extends Query<AuthState> {
  protected override store: AuthStore;

  // GLOBAL
  verified: boolean = false;

  // User Specific Selectors
  id$ = this.select((state) => state.id);
  token$ = this.select((state) => state.token);
  //idWith user only changes if user id changes
  idWithUser$ = this.select((state) => state.id).pipe(
    filter((_) => !!this.getValue().user),
    map((_) => this.getValue().user),
  );
  user$ = this.select((state) => state.user).pipe(filter((user) => !!user));
  isLoggedIn$ = this.select((state) => toBoolean(state.token));
  isAnonUser$ = this.select((state) => toBoolean(state.id === ANON_USER.id));
  permissions$ = this.select((state) => state.permissions);
  permissionsWithUser$ = this.select((state) => state.permissions).pipe(
    filter((_) => !!this.getValue().user),
    map((_) => ({ user: this.getValue().user, products: this.getValue().products, permissions: this.getValue().permissions })),
  );
  cookieConsent$ = this.select((state) => get(state, 'user.cookieConsent', false));
  newsletterConsent$ = this.select((state) => get(state, 'user.newsletterConsent', { accepted: false, date: null }));
  products$ = this.select((state) => state.products);
  productsWithUser$ = this.select((state) => state.products).pipe(
    filter((_) => !!this.getValue().user),
    map((_) => ({ user: this.getValue().user, products: this.getValue().products, permissions: this.getValue().permissions })),
  );

  // Preferences Specific Selectors (Observables)
  preferences$ = this.select((state) => get(state, 'user.userPreferences', null));
  language$ = this.select((state) => get(state, 'user.userPreferences.uiLanguage', null));
  colorSets$ = this.select((state) => state.user.defaultStyles);
  theme$ = this.select((state) => get(state, 'user.userPreferences.theme', 'auto'));
  editorSettings$ = this.select((state) => get(state, 'user.userPreferences.editor', {}));
  logo$ = this.select((state) => get(state, 'user.userPreferences.logo', null));
  shortCuts$ = this.select((state) => get(state, 'user.userPreferences.shortCuts', []));

  profilePicture$ = this.select((state) => get(state, 'user.userPreferences.profilePicture', null));
  defaultContentLogo$ = this.select((state) => get(state, 'user.userPreferences.defaultContentLogo', null));

  // hideHelpInfo$ = this.select((state) => get(state, 'user.userPreferences.hideHelp', false));

  dpa$ = this.select((state) => get(state, 'user.userPreferences.externalProvider.dpaid', false));

  mediaView$ = this.select((state) => get(state, 'user.userPreferences.mediaViewMode'));

  newContentPagePermission$ = this.select((state) => state.permissions.some((permission) => permission.resource === PERMISSIONS.NEW_CONTENT_PAGE));
  newContentPage$ = this.select(
    (state) => state.permissions.some((permission) => permission.resource === PERMISSIONS.NEW_CONTENT_PAGE) && state.user.userPreferences.newContentPage,
  );

  oldContentPage$ = this.select(
    (state) => !state.permissions.some((permission) => permission.resource === PERMISSIONS.NEW_CONTENT_PAGE) || !state.user.userPreferences.newContentPage,
  );

  constructor() {
    const store = inject(AuthStore);

    super(store);
    this.store = store;
  }

  //get Functions that return the current value
  //dont use this for things that can change but only one off settings
  // Plain get values
  user() {
    const user = this.getValue().user;
    // console.log('[AuthStore] - getting current User', user.name);
    return user;
  }
  token() {
    const token = this.getValue().token;
    //console.log("[AuthStore] - getting current Token",token);
    return token;
  }
  id() {
    const id = this.getValue().id;
    //console.log("[AuthStore] - getting current UserID",id);
    return id;
  }

  getEditorSettings() {
    return get(this.getValue(), 'user.userPreferences.editor', {});
  }

  isLoggedIn() {
    return toBoolean(this.getValue().token) && !this.isAnonUser();
  }

  isAnonUser() {
    return this.getValue().id === ANON_USER.id;
  }

  hasProduct(product: Product) {
    const products = this.getValue().products;

    if (product && products && products.length > 0) {
      return products.findIndex((userProduct) => userProduct.slug === product.slug) > -1;
    }
    return false;
  }

  checkPermission(permission) {
    const current = this.getValue();
    const permissions = current.permissions;
    //console.log("[checkPermission with state: ]",JSON.parse(JSON.stringify(current)));
    if (permissions && permissions.length > 0) {
      return permissions.findIndex((userPermission) => userPermission.resource === permission) > -1;
    }
    return false;
  }

  hasPermission(permission) {
    return this.checkPermission(permission);
  }

  checkConsent() {
    if (this.isAnonUser()) {
      return storage.getConsent();
    }
    return this.getValue().user.cookieConsent || storage.getConsent();
  }

  checkNewsletterConsentGiven() {
    const user = this.getValue().user;
    return user?.newsletterConsent?.date !== null;
  }
}
